import classNames from 'classnames';
import {useState} from 'react';
import ReactPlayer from 'react-player/file';
import {JsonLd} from 'react-schemaorg';
import type {VideoObject} from 'schema-dts';

import {Button, LinkButton} from '@/button';
import FontAwesomeV6Icon from '@/fontAwesomeV6Icon';
import {BodyTwoText, BodyThreeText, Figcaption, StrongText} from '@/typography';
import Facade from '@/video/Facade';
import NativeVideo from '@/video/NativeVideo';
import {RenderState, VideoProps} from '@/video/types';
import YouTubeVideo from '@/video/YoutubeVideo';

import moduleStyles from './video.module.scss';

/**
 * ### Production-ready Checklist:
 * * (✔) implementation of component approved by design team;
 * * (✔) has storybook, covered with stories and documentation;
 * * (✔) has tests: test every prop, every state and every interaction that's js related;
 * * (see ./__tests__/Video.test.tsx)
 * * (✔) passes accessibility checks;
 *
 * ###  Status: ```Ready for dev```
 *
 * Design System: Video Component.
 * This component is used to display a video from YouTube with a fallback HTML video player,
 * and the option to download it, if an externally hosted fallback is provided.
 * The video can also be displayed with a caption and works with responsive screen sizes.
 */
const Video: React.FC<VideoProps> = ({
  youTubeId,
  videoTitle,
  videoDesc,
  videoFallback,
  showCaption,
  downloadLabel,
  uploadDate,
  errorHeading,
  errorBody,
  className,
  isYouTubeCookieAllowed,
}: VideoProps) => {
  const youtubeVideoUrl = `https://www.youtube-nocookie.com/watch?v=${youTubeId}`;

  const [renderState, setRenderState] = useState<RenderState>('facade');

  // Prefer the 1280x720 thumbnail; videos uploaded in SD don't have one, and
  // YouTube then serves a 120x90 placeholder (rendered despite the 404), so
  // detect it by size and fall back to the always-available 480x360.
  const [useFallbackThumbnail, setUseFallbackThumbnail] = useState(false);
  const posterThumbnail = `//i.ytimg.com/vi/${youTubeId}/${
    useFallbackThumbnail ? 'hqdefault' : 'maxresdefault'
  }.jpg`;

  const handlePosterLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (event.currentTarget.naturalWidth <= 120) {
      setUseFallbackThumbnail(true);
    }
  };

  const handleError = (
    error: Error | undefined,
    nextRenderState: RenderState,
  ) => {
    // If blocked due to an interaction autoplay issue, don't move to the next render state but allow the user to
    // manually click the play button
    if (error?.name === 'NotAllowedError') {
      console.warn(error);
    } else {
      setRenderState(nextRenderState);
    }
  };

  const handleFacadeClick = () => {
    if (isYouTubeCookieAllowed && !window.CDOVideoPlayer?.isYouTubeBlocked) {
      setRenderState('youtube');
    } else {
      if (videoFallback && ReactPlayer.canPlay(videoFallback)) {
        setRenderState('native');
      } else {
        if (window.CDOVideoPlayer?.isYouTubeBlocked) {
          setRenderState('error');
        } else {
          setRenderState('cookie-blocked');
        }
      }
    }
  };

  const getVideoPlayer = () => {
    switch (renderState) {
      case 'facade':
        return (
          <Facade
            label={`Play video ${videoTitle}`}
            posterThumbnail={posterThumbnail}
            onClick={handleFacadeClick}
            onPosterLoad={handlePosterLoad}
          />
        );
      case 'youtube':
        return (
          <YouTubeVideo
            posterThumbnail={posterThumbnail}
            videoTitle={videoTitle}
            src={youtubeVideoUrl}
            onError={error => {
              const nextRenderState =
                videoFallback && ReactPlayer.canPlay(videoFallback)
                  ? 'native'
                  : 'error';

              handleError(error, nextRenderState);
            }}
          />
        );
      case 'native':
        return (
          <NativeVideo
            posterThumbnail={posterThumbnail}
            videoTitle={videoTitle}
            src={videoFallback}
            className={className}
            onError={error => handleError(error, 'error')}
          />
        );
      case 'error':
        return (
          <div className={classNames(moduleStyles.errorPlaceholder)}>
            <FontAwesomeV6Icon
              iconName="exclamation-circle"
              iconStyle="solid"
            />
            <BodyTwoText>
              <StrongText>{errorHeading || 'Video unavailable'}</StrongText>
            </BodyTwoText>
            <BodyThreeText>
              {errorBody || 'This video is blocked on your network.'}
            </BodyThreeText>
          </div>
        );
      case 'cookie-blocked':
        return (
          <div className={classNames(moduleStyles.errorPlaceholder)}>
            <FontAwesomeV6Icon
              iconName="exclamation-circle"
              iconStyle="solid"
            />
            <BodyTwoText>
              <StrongText>
                {errorHeading || 'Cookie consent required'}
              </StrongText>
            </BodyTwoText>
            <BodyThreeText>
              {errorBody ||
                'Please enable "Functional Cookies" and refresh the page to play this video.'}
            </BodyThreeText>
            <Button
              className={moduleStyles.cookieConsentButton}
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).OneTrust.ToggleInfoDisplay();
              }}
              text={'Cookie Settings'}
            />
          </div>
        );
    }
  };
  return (
    <figure
      className={classNames(moduleStyles.videoComponentContainer, className)}
    >
      <div className={moduleStyles.videoWrapper}>{getVideoPlayer()}</div>
      <div className={moduleStyles.footer}>
        {showCaption && <Figcaption>{videoTitle}</Figcaption>}
        {videoFallback && (
          <LinkButton
            className={moduleStyles.download}
            color="black"
            href={videoFallback}
            iconLeft={{
              iconName: 'download',
              iconStyle: 'solid',
            }}
            size="s"
            text={downloadLabel || 'Download'}
            type="secondary"
            target="_blank"
            rel="noopener noreferrer"
          />
        )}
      </div>

      {/* JSON-LD for structured data. Needed for Google SEO.
      (see https://developers.google.com/search/docs/appearance/structured-data/video#json-ld) */}
      {videoTitle && posterThumbnail && uploadDate && (
        <JsonLd<VideoObject>
          item={{
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: videoTitle,
            description: videoDesc,
            thumbnailUrl: posterThumbnail,
            uploadDate: uploadDate,
            embedUrl: youtubeVideoUrl,
            contentUrl: videoFallback,
          }}
        />
      )}
    </figure>
  );
};

export default Video;
